import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { DEFAULT_ROWS_PER_PAGE, type RowsPerPage } from '../../components/RowsPerPageSelect';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { GhostButton } from '../Dashboard/components/GhostButton';
import { MetricTile } from '../Dashboard/components/MetricTile';
import { CARD_SX, WIDA_LEVELS } from '../Dashboard/tokens';
import {
  fetchRosterClassification,
  type RosterClassificationRow,
} from '../Roster/rosterClassification';
import { departedEmptyState, scopeByEnrollment } from '../Roster/enrollmentScope';
import {
  classificationToRosterRow,
  filterRowsBySchool,
  pageRows,
  sortRowsByName,
  type RosterRow,
} from '../Roster/rosterRows';
import { RosterTable } from '../Roster/RosterTable';
import { StackedDomainBar } from '../Dashboard/components/StackedDomainBar';
import { DOMAIN_LABELS, DOMAIN_KEYS } from '../Roster/rosterRows';
import { loadThresholds } from '../../core/thresholds';
import { useDistrictYears } from '../../core/useDistrictYears';
import { useProgramEvaluation, type PeDataSource } from '../ProgramEvaluation/program-evaluation-hook';
import type { ScoreUnit } from '../ProgramEvaluation/types';
import { districtAverages, enrolmentRank, findPeSchool } from './schoolPeInsights';
import { findSchoolDetail } from './schoolDetail';
import { SchoolColumnChart } from './SchoolColumnChart';
import {
  approachingLtelCount,
  domainDataQuality,
  gradeDistribution,
  proficiencyProfile,
  teacherCaseloads,
  yearsInProgram,
} from './schoolInsights';
import { SCHOOLS } from './tokens';
import { PAGE_MAX_WIDTH } from '../../theme';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';


function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

const EM_DASH = '—';

function formatPercent(fraction: number | null | undefined): string {
  return fraction == null ? EM_DASH : `${Math.round(fraction * 100)}%`;
}

function formatGrowth(
  unit: ScoreUnit,
  scaleScoreDelta: number | null | undefined,
  levelDelta: number | null | undefined,
): string {
  if (unit === 'scale_score') {
    return scaleScoreDelta == null ? EM_DASH : `${scaleScoreDelta >= 0 ? '+' : ''}${scaleScoreDelta} pts`;
  }
  return levelDelta == null ? EM_DASH : `${levelDelta >= 0 ? '+' : ''}${levelDelta.toFixed(1)} levels`;
}

type Props = {
  classificationSource?: () => Promise<RosterClassificationRow[] | null>;
  peSource?: PeDataSource;
};

export function SchoolDetailPage({
  classificationSource = fetchRosterClassification,
  peSource,
}: Props = {}) {
  const navigate = useNavigate();
  const { schoolName = '' } = useParams();
  const [rows, setRows] = useState<RosterRow[]>([]);
  const [accessUploaded, setAccessUploaded] = useState(false);
  const [departedOnly, setDepartedOnly] = useState(false);
  const [failed, setFailed] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPage>(DEFAULT_ROWS_PER_PAGE);

  useEffect(() => {
    let active = true;
    classificationSource()
      .catch(() => null)
      .then((classified) => {
        if (!active) return;
        setFailed(classified === null);
        const all = (classified ?? []).map(classificationToRosterRow);
        setAccessUploaded(all.some((row) => row.noPlan !== true));
        setRows(scopeByEnrollment(all, false));
        setDepartedOnly(departedEmptyState(filterRowsBySchool(all, schoolName), false));
        setHydrated(true);
      });
    return () => {
      active = false;
    };
  }, [classificationSource, schoolName]);

  const detail = useMemo(() => findSchoolDetail(rows.filter((row) => row.heldOut !== true), schoolName), [rows, schoolName]);

  const students = useMemo(
    () => sortRowsByName(filterRowsBySchool(rows, schoolName), sortDirection),
    [rows, schoolName, sortDirection],
  );

  const totalPages = Math.max(1, Math.ceil(students.length / rowsPerPage));

  const { years: districtYears } = useDistrictYears();
  const thresholds = useMemo(() => districtYears ?? loadThresholds(), [districtYears]);
  const answered = useMemo(() => students.filter((row) => row.heldOut !== true), [students]);
  const grades = useMemo(() => gradeDistribution(answered), [answered]);
  const years = useMemo(() => yearsInProgram(answered, thresholds), [answered, thresholds]);
  const approaching = useMemo(
    () => approachingLtelCount(answered, thresholds),
    [answered, thresholds],
  );
  const profile = useMemo(() => proficiencyProfile(answered), [answered]);
  const caseloads = useMemo(() => teacherCaseloads(answered), [answered]);
  const quality = useMemo(() => domainDataQuality(answered), [answered]);

  const { report, content } = useProgramEvaluation(peSource);
  const bySchool = useMemo(
    () => (report?.cannot_generate === true ? [] : (report?.by_school ?? [])),
    [report],
  );
  const unit: ScoreUnit = content?.unit_mode ?? 'proficiency_level';
  const peSchool = useMemo(() => findPeSchool(bySchool, schoolName), [bySchool, schoolName]);
  const district = useMemo(() => districtAverages(bySchool), [bySchool]);
  const rank = useMemo(() => enrolmentRank(bySchool, schoolName), [bySchool, schoolName]);

  const subtitle =
    peSchool == null || peSchool.total_enrollment == null
      ? null
      : [
          peSchool.school_type,
          `${peSchool.el_count} EL students of ${peSchool.total_enrollment.toLocaleString('en-US')} enrolled (${formatPercent(peSchool.el_share_of_enrollment)})`,
        ]
          .filter((part): part is string => part != null && part !== '')
          .join(' · ');

  const rankDetail =
    rank == null
      ? null
      : rank.rank === 1
        ? `Largest of ${rank.total} ${plural(rank.total, 'school', 'schools')}`
        : `#${rank.rank} of ${rank.total} by EL enrolment`;

  function shareDetail(schoolShare: number | null, districtShare: number | null): string | null {
    if (schoolShare == null || districtShare == null) return null;
    return `${formatPercent(schoolShare)} of ELs · district ${formatPercent(districtShare)}`;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: SCHOOLS.surface }}>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="schools" />
      <Box component="main" sx={{ flex: 1, minWidth: 0, p: '32px 24px 72px' }}>
        <Box
          data-testid="school-detail-content"
          sx={{
            maxWidth: PAGE_MAX_WIDTH,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          <Box>
            <GhostButton
              label="Back to Schools"
              onClick={() => navigate('/schools')}
              startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            />
          </Box>

          <Box
            data-testid="school-detail-card"
            sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', gap: '20px' }}
          >
            <Typography
              component="h1"
              sx={{
                fontFamily: SCHOOLS.font,
                fontSize: 28,
                fontWeight: 600,
                lineHeight: '36px',
                color: SCHOOLS.ink,
              }}
            >
              {detail?.name ?? schoolName}
            </Typography>
            {subtitle != null && (
              <Typography
                component="div"
                sx={{
                  mt: '-12px',
                  fontFamily: SCHOOLS.font,
                  fontSize: 16,
                  fontWeight: 500,
                  lineHeight: '24px',
                  color: SCHOOLS.muted,
                }}
              >
                {subtitle}
              </Typography>
            )}
            {detail && (
              <Box sx={{ display: 'flex', gap: '16px', '& > *': { flex: 1 } }}>
                {[
                  { label: 'Students', value: detail.students, detail: rankDetail, warn: false },
                  {
                    label: 'Newcomers',
                    value: detail.newcomers,
                    detail: shareDetail(
                      peSchool?.newcomer_share_of_school_els ?? null,
                      district.newcomerShare,
                    ),
                    warn: false,
                  },
                  {
                    label: 'LTELs',
                    value: detail.ltels,
                    detail: shareDetail(
                      peSchool?.ltel_share_of_school_els ?? null,
                      district.ltelShare,
                    ),
                    warn:
                      peSchool != null &&
                      district.ltelShare != null &&
                      peSchool.ltel_share_of_school_els > district.ltelShare,
                  },
                ].map(({ label, value, detail: tileDetail, warn }) => (
                  <Box
                    key={label}
                    data-testid="school-metric"
                    sx={{ display: 'flex', '& > *': { flex: 1 } }}
                  >
                    <MetricTile
                      value={String(value)}
                      label={label}
                      detail={
                        tileDetail == null ? undefined : (
                          <Box
                            component="span"
                            sx={{
                              fontFamily: SCHOOLS.font,
                              fontSize: 12,
                              fontWeight: 700,
                              lineHeight: '16px',
                              color: warn ? SCHOOLS.emphasis : SCHOOLS.compareInk,
                            }}
                          >
                            {tileDetail}
                          </Box>
                        )
                      }
                    />
                  </Box>
                ))}
              </Box>
            )}
            {detail && approaching > 0 && (
              <Box
                data-testid="ltel-pipeline-alert"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                  p: '12px 16px',
                  borderRadius: '8px',
                  bgcolor: SCHOOLS.alertBg,
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <Typography
                    component="div"
                    sx={{
                      fontFamily: SCHOOLS.font,
                      fontSize: 16,
                      fontWeight: 700,
                      lineHeight: '24px',
                      color: SCHOOLS.emphasis,
                    }}
                  >
                    {`${approaching} ${plural(approaching, 'student becomes', 'students become')} LTEL next year`}
                  </Typography>
                  <Typography
                    component="div"
                    sx={{
                      fontFamily: SCHOOLS.font,
                      fontSize: 12,
                      fontWeight: 500,
                      lineHeight: '16px',
                      color: SCHOOLS.alertDetail,
                    }}
                  >
                    {`They reach year ${thresholds.ltelMinYears} in the program next year`}
                  </Typography>
                </Box>
                <GhostButton
                  label="View these students"
                  onClick={() => navigate(`/roster?school=${encodeURIComponent(detail.name)}`)}
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                />
              </Box>
            )}
            {hydrated && !detail && (
              <Typography
                component="div"
                sx={{
                  fontFamily: SCHOOLS.font,
                  fontSize: 16,
                  lineHeight: '24px',
                  color: SCHOOLS.muted,
                }}
              >
                {failed
                  ? 'We could not load your data. Nothing has been lost. Refresh the page to try again.'
                  : departedOnly
                    ? 'No active students at this school.'
                    : 'School not found.'}
              </Typography>
            )}
          </Box>

          {detail && (
            <Box
              data-testid="school-detail-card"
              sx={{ ...CARD_SX, p: '24px 8px', display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <Typography
                component="div"
                sx={{
                  px: '16px',
                  fontFamily: SCHOOLS.font,
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: '24px',
                  color: SCHOOLS.muted,
                }}
              >
                Students
              </Typography>
              <RosterTable
                rows={pageRows(students, page, rowsPerPage)}
                sortDirection={sortDirection}
                onToggleSort={() => setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))}
                onOpenRow={(id) => navigate(`/roster/${encodeURIComponent(id)}`)}
                accessUploaded={accessUploaded}
                page={page}
                totalRows={students.length}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(next) => {
                  setRowsPerPage(next);
                  setPage(1);
                }}
                onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
                onNextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
              />
            </Box>
          )}

          {detail && peSchool != null && (
            <Box
              data-testid="school-detail-card"
              sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <Typography
                component="div"
                sx={{
                  fontFamily: SCHOOLS.font,
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: '24px',
                  color: SCHOOLS.muted,
                }}
              >
                Outcomes
              </Typography>
              <Box
                data-testid="outcomes-card"
                sx={{ display: 'flex', gap: '16px', '& > *': { flex: 1 } }}
              >
                <MetricTile
                  value={formatGrowth(
                    unit,
                    peSchool.composite_growth_scale_score_delta,
                    peSchool.composite_growth_proficiency_level_delta,
                  )}
                  label="Composite growth"
                />
                <MetricTile
                  value={formatPercent(peSchool.reclassification_rate)}
                  label="Reclassification rate"
                  detail={
                    district.reclassificationRate == null ? undefined : (
                      <Box
                        component="span"
                        sx={{
                          px: '10px',
                          py: '4px',
                          borderRadius: '9999px',
                          bgcolor: SCHOOLS.positiveChipBg,
                          fontFamily: SCHOOLS.font,
                          fontSize: 12,
                          fontWeight: 700,
                          lineHeight: '16px',
                          color: SCHOOLS.positiveChipFg,
                        }}
                      >
                        {`District ${formatPercent(district.reclassificationRate)}`}
                      </Box>
                    )
                  }
                />
              </Box>
            </Box>
          )}

          {detail && (
            <Box
              data-testid="school-detail-card"
              sx={{ ...CARD_SX, display: 'flex', gap: '32px', alignItems: 'stretch' }}
            >
              <SchoolColumnChart
                title="EL students by grade"
                columns={grades.map((grade) => ({ label: grade.grade, value: grade.count }))}
              />
              <SchoolColumnChart
                title="Years in the EL program"
                thresholdAfterIndex={years.length - 2}
                columns={years.map((bucket) => ({
                  label: bucket.label,
                  value: bucket.count,
                  emphasised: bucket.isApproaching,
                  color: bucket.isLtel
                    ? SCHOOLS.ltelBar
                    : bucket.isApproaching
                      ? SCHOOLS.approachingBar
                      : SCHOOLS.ghost,
                }))}
              />
            </Box>
          )}

          {detail && (
            <Box
              data-testid="school-detail-card"
              sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <Typography
                component="div"
                sx={{
                  fontFamily: SCHOOLS.font,
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: '24px',
                  color: SCHOOLS.muted,
                }}
              >
                Proficiency profile
              </Typography>
              <Box
                data-testid="proficiency-card"
                sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
              >
                {DOMAIN_KEYS.map((domain) => (
                  <Box key={domain} data-testid="proficiency-domain">
                    <StackedDomainBar domain={DOMAIN_LABELS[domain]} counts={profile[domain]} />
                  </Box>
                ))}
                <Box sx={{ pl: '88px', display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                  {WIDA_LEVELS.map((level) => (
                    <Box
                      key={level.key}
                      sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          flexShrink: 0,
                          borderRadius: '2px',
                          bgcolor: level.color,
                        }}
                      />
                      <Box
                        component="span"
                        sx={{
                          fontFamily: SCHOOLS.font,
                          fontSize: 12,
                          fontWeight: 600,
                          lineHeight: '16px',
                          color: SCHOOLS.muted,
                        }}
                      >
                        {level.label}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          {detail && (
            <Box
              data-testid="school-detail-card"
              sx={{ ...CARD_SX, display: 'flex', gap: '16px', alignItems: 'stretch' }}
            >
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Typography
                  component="div"
                  sx={{
                    fontFamily: SCHOOLS.font,
                    fontSize: 16,
                    fontWeight: 700,
                    lineHeight: '24px',
                    color: SCHOOLS.muted,
                  }}
                >
                  Teacher caseloads
                </Typography>
                {caseloads.map((load) => (
                  <Box
                    key={load.teacher}
                    data-testid="teacher-caseload"
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: '12px',
                      pb: '8px',
                      borderBottom: `1px solid ${SCHOOLS.border}`,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontFamily: SCHOOLS.font,
                        fontSize: 16,
                        fontWeight: 500,
                        lineHeight: '24px',
                        color: SCHOOLS.ink,
                      }}
                    >
                      {load.teacher}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontFamily: SCHOOLS.font,
                        fontSize: 24,
                        fontWeight: 800,
                        lineHeight: '32px',
                        letterSpacing: '-0.0156em',
                        color: SCHOOLS.ink,
                      }}
                    >
                      {load.students}
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  p: '16px',
                  borderRadius: '8px',
                  bgcolor: SCHOOLS.surface,
                  border: `2px dashed ${SCHOOLS.border}`,
                }}
              >
                <Typography
                  component="div"
                  sx={{
                    fontFamily: SCHOOLS.font,
                    fontSize: 16,
                    fontWeight: 700,
                    lineHeight: '24px',
                    color: SCHOOLS.ink,
                  }}
                >
                  {`${quality.studentsMissingAny} ${plural(quality.studentsMissingAny, 'student', 'students')} missing a domain score`}
                </Typography>
                {quality.byDomain.map(({ domain, missing }) => (
                  <Box
                    key={domain}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontFamily: SCHOOLS.font,
                        fontSize: 14,
                        fontWeight: 600,
                        lineHeight: '20px',
                        color: SCHOOLS.muted,
                      }}
                    >
                      {DOMAIN_LABELS[domain]}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontFamily: SCHOOLS.font,
                        fontSize: 14,
                        fontWeight: 700,
                        lineHeight: '20px',
                        color: missing > 0 ? SCHOOLS.emphasis : SCHOOLS.muted,
                      }}
                    >
                      {missing > 0 ? `${missing} missing` : 'Complete'}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
